<script>
  import { createEventDispatcher, onDestroy } from 'svelte';
  import { fade } from 'svelte/transition';
  import { listenToChannel } from '../../Api/api';
  import { firebaseApp } from '../../Store/firebase-store';
  import DoubleBounce from '../Common/DoubleBounce.svelte';
  import Player from './Player.svelte';

  const dispatch = createEventDispatcher();

  onDestroy(() => {
    videoPlaying = false;
    videoLoadingMsgIndex = 0;
    if (videoLoadingMsgInterval) {
      clearInterval(videoLoadingMsgInterval);
      videoLoadingMsgInterval = null;
    }
  });

  export let id = '';
  export let muteAudio;
  export let websocketURL = '';
  export let hlsURL;

  // Even if playout is HLS, Chat admin should show WebRTC stream for accurate preview
  export let forceWebRTC = false;

  let videoPlaying = false;

  let videoLoadingMessages = [
    'Om et lite øyeblikk går vi live med video i chatten. 🤩',
    'Takk for at du deltar!',
    'Heng med, snart klart!',
    'Hvert øyeblikk nå...'
  ];
  let infoText = '';
  let videoLoadingMsgIndex = 0;
  let videoLoadingMsgInterval;

  $: channel && channel.roomId && !videoPlaying && enableInfoBadge();

  const enableInfoBadge = () => {
    // Chat is hosted in Admin
    if (forceWebRTC) {
      infoText = 'Forhåndsvisning er snart klar.';
    } else {
      infoText = videoLoadingMessages[videoLoadingMsgIndex];
      videoLoadingMsgInterval = setInterval(() => {
        videoLoadingMsgIndex += 1;
        if (videoLoadingMsgIndex > videoLoadingMessages.length - 1) {
          videoLoadingMsgIndex = videoLoadingMessages.length - 1;
        }
        infoText = videoLoadingMessages[videoLoadingMsgIndex];
      }, 3000);
    }
  };

  export const playVideo = () => {
    if (player) {
      player.playVideo();
    }
  };

  export const cancelVideo = () => {
    if (player) {
      player.stop(streamName);
    }
  };

  const onPlayfinished = () => {
    if (channel && channel.roomId && player) {
      streamName = channel.roomId;
      playout = forceWebRTC
        ? 'WebRTC'
        : channel.playout
        ? channel.playout
        : 'WebRTC';
      player.stop(playout, streamName);
      const options = getPlayoutOptions(playout);
      player.init(options, () => {
        player.play(playout, streamName);
      });
    }
  };

  let unsubscribeListenToChannel = null;
  let streamName = '';
  let playout = '';
  let player;
  let stopHLSTimer = null;

  $: channel = null;
  $: id && listenToNewChannelData(id);

  const listenToNewChannelData = async (channelId) => {
    if (unsubscribeListenToChannel) {
      unsubscribeListenToChannel();
      unsubscribeListenToChannel = null;
    }
    unsubscribeListenToChannel = await listenToChannel(
      channelId,
      (snapShot) => {
        channel = snapShot.data();
        if (channel && player) {
          if (channel.roomId) {
            if (stopHLSTimer) {
              clearInterval(stopHLSTimer);
              player.stop('HLS', streamName);
              stopHLSTimer = null;
            }
            streamName = channel.roomId;
            playout = forceWebRTC
              ? 'WebRTC'
              : channel.playout
              ? channel.playout
              : 'WebRTC';
            const options = getPlayoutOptions(playout);
            player.init(options, () => {
              player.play(playout, streamName);
            });
          } else if (streamName) {
            if (playout === 'HLS') {
              stopHLSTimer = setTimeout(
                () => player.stop('HLS', streamName),
                8000
              );
            } else {
              player.stop(playout, streamName);
            }
            streamName = '';
            playout = '';
          }
        }
      },
      $firebaseApp
    );
  };

  const getPlayoutOptions = (playout) => {
    if (playout === 'HLS') {
      const src = hlsURL.replace('ROOM_ID', `${streamName}_720p.m3u8`);
      return { src, playout };
    } else if (playout === 'WebRTC') {
      return { src: websocketURL, playout };
    }
  };

  const keyCodesDebug = [68, 69, 66, 85, 71];
  let keyCodesInput = [];
  let index = 0;
  let debugMode = false;
  let streamResolutions = [];
  let selectedQuality = null;
  const handleKeydown = (e) => {
    if (e.shiftKey && e.ctrlKey) {
      if (e.keyCode === keyCodesDebug[index]) {
        keyCodesInput.push(e.keyCode);
        index++;
      } else {
        keyCodesInput = [];
        index = 0;
      }
    }
    if (
      keyCodesInput.length === keyCodesDebug.length &&
      keyCodesInput.every((w, i) => keyCodesDebug[i] === w)
    ) {
      debugMode = true;
      index = 0;
      keyCodesInput = [];
    }
  };

  const onStreamResolutions = (e) => {
    streamResolutions = e.detail;
  };

  const onStreamResolutionChanged = (e) => {
    forceStreamQuality(channel.roomId, e.target.value);
  };

  const forceStreamQuality = (streamId, value) => {
    player.forceStreamQuality(streamId, value);
  };

  const onUpdatedStats = (e) => {
    const { frameHeight } = e.detail.stats;
    const curQuality = e.detail.currentQuality;
    let nextResIndex = streamResolutions.indexOf(curQuality) - 1;
    if (nextResIndex < 0) {
      nextResIndex = streamResolutions.length - 1;
    }
    if (frameHeight !== curQuality || frameHeight === -1) {
      selectedQuality = streamResolutions[nextResIndex];
      forceStreamQuality(channel.roomId, selectedQuality);
    } else {
      selectedQuality = frameHeight;
      if (player) {
        player.disableStats(channel.roomId);
      }
    }
  };

  const onVideoPlaying = () => {
    videoPlaying = true;
    dispatch('video-playing');
  };

  const onVideoDone = () => {
    videoPlaying = false;
    videoLoadingMsgIndex = 0;
    if (videoLoadingMsgInterval) {
      clearInterval(videoLoadingMsgInterval);
      videoLoadingMsgInterval = null;
    }
    dispatch('video-done');
  };
</script>

<svelte:window on:keydown={handleKeydown} />

<div class="player-container">
  <Player
    {muteAudio}
    {websocketURL}
    bind:this={player}
    on:video-ready
    on:video-done={onVideoDone}
    on:video-playing={onVideoPlaying}
    on:updatedstats={onUpdatedStats}
    on:streamresolutions={onStreamResolutions}
    on:playfinsihed={onPlayfinished}
  />
  {#if channel && channel.roomId && !videoPlaying}
    <div class="info-badge" in:fade out:fade>
      <div class="info-content">
        <div class="anim">
          <DoubleBounce size={40} />
        </div>
        <p>{infoText}</p>
      </div>
    </div>
  {/if}
  {#if debugMode}
    <div class="debug">
      <select
        bind:value={selectedQuality}
        on:change={onStreamResolutionChanged}
      >
        {#each streamResolutions as resolution}
          <option value={resolution}>{resolution}</option>
        {/each}
      </select>
    </div>
  {/if}
</div>

<style src="./playerContainer.scss"></style>
